import { morphSection } from '@theme/section-renderer';
import { MEMBER_CODE, memberCodeAction, safeCartEndpoint } from './timecard-member-cart-core.js';

// Only submits the public code to Shopify's cart API. DiscountOS/Shopify enforce
// identity, consent, order history and one-use limits independently of this UI.
const attempted = new Set();
const results = new Map();
let inFlight = false;
class TimecardMemberCart extends HTMLElement {
  static observedAttributes = ['data-signature', 'data-pending'];
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    queueMicrotask(() => this.sync());
  }
  disconnectedCallback() { this.removeEventListener('click', this.onClick); }
  attributeChangedCallback() { if (this.isConnected) queueMicrotask(() => this.sync()); }
  onClick = event => {
    if (event.target.closest('[data-member-cart-retry]')) {
      attempted.delete(this.dataset.signature);
      results.delete(this.dataset.signature);
      this.sync();
    }
  };
  message(text) {
    document.querySelectorAll('[data-member-cart-status]').forEach(node => { node.textContent = text; });
  }
  async sync() {
    if (this.dataset.pending !== 'true') return;
    const signature = this.dataset.signature;
    if (results.has(signature)) { this.message(results.get(signature)); return; }
    if (inFlight || attempted.has(signature)) return;
    attempted.add(signature);
    inFlight = true;
    this.message('Checking your first-order savings… Your current shipping progress is shown above.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    const finish = text => { results.set(signature, text); this.message(text); };
    const buttons = [...document.querySelectorAll('[data-member-cart-retry]')];
    buttons.forEach(button => { button.disabled = true; });
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(safeCartEndpoint(root, 'cart.js', location.origin), {credentials:'same-origin', signal:controller.signal, headers:{Accept:'application/json'}});
      if (!cartResponse.ok) throw Error('CART_READ_FAILED');
      const cart = await cartResponse.json();
      const action = memberCodeAction(cart);
      if (action === 'empty') return;
      if (action === 'other-code') {
        finish('Another promotion is selected. First-order savings cannot be combined with it. Remove that code to check member savings.');
        return;
      }
      const sections = [...new Set([...document.querySelectorAll('timecard-member-cart')].map(node=>node.dataset.sectionId || node.closest('[id^="shopify-section-"]')?.id.replace(/^shopify-section-/, '')).filter(Boolean))].slice(0,5);
      if (!sections.length) throw Error('CART_SECTION_MISSING');
      const response = await fetch(safeCartEndpoint(root, 'cart/update.js', location.origin), {
        method:'POST', credentials:'same-origin', signal:controller.signal, headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({discount:MEMBER_CODE,sections,sections_url:location.pathname})
      });
      if (!response.ok) throw Error('CART_UPDATE_FAILED');
      const data = await response.json();
      const applied = (data.discount_codes || []).some(d=>String(d.code).toUpperCase()===MEMBER_CODE && d.applicable === true);
      results.set(signature, applied ? 'Member savings updated.' : 'The first-order offer is not currently applicable to this cart. Shipping progress uses your current cart total.');
      for (const section of sections) {
        const html = data.sections?.[section];
        if (typeof html !== 'string' || !html) throw Error('CART_RENDER_FAILED');
        await morphSection(section, html, {mode:'full'});
      }
      this.message(results.get(signature));
    } catch {
      finish('Member savings could not be refreshed. Try again below. Shipping progress uses the current cart total; final eligibility is checked at checkout.');
    } finally {
      clearTimeout(timeout);
      inFlight = false;
      document.querySelectorAll('[data-member-cart-retry]').forEach(button => { button.disabled = false; });
    }
  }
}
if (!customElements.get('timecard-member-cart')) customElements.define('timecard-member-cart', TimecardMemberCart);
